import React, { useState, useEffect } from 'react';
import { Card, Table, Input, Select, Button, Space, DatePicker, Tag, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { format, parseISO } from 'date-fns';
import getSystemLogs from '../../../services/systemLogs';
import userService from '../../../services/userService';
import { Skeleton } from 'antd';

const { RangePicker } = DatePicker;
const { Option } = Select;

const UserCell = ({ userId }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const response = await userService.getUser(userId);
        setUser(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  if (loading) return <Skeleton />;
  if (error) return <div>Error fetching user</div>;
  return <div>{user?.full_name}</div>;
};

const SystemLogs = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    level: '',
    dateRange: [],
  });

  // Fetch logs from API
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { current, pageSize } = pagination;
      const params = {
        skip: (current - 1) * pageSize,
        limit: pageSize,
      };

      // Add filters to params if they exist
      if (filters.level) params.level = filters.level;
      if (filters.source) params.source = filters.source;
      if (filters.search) params.search = filters.search;
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].toISOString();
        params.end_date = filters.dateRange[1].toISOString();
      }

      //axios.get('/api/v1/system-logs/', { params });
      const response = await getSystemLogs(params)
      const { items, total, page, page_size, total_pages } = response;

      setLogs(items);
      setPagination({
        ...pagination,
        current: page,
        pageSize: page_size,
        total,
        totalPages: total_pages,
      });
    } catch (error) {
      console.error('Error fetching logs:', error);
      message.error('Failed to fetch system logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [pagination.current, pagination.pageSize, JSON.stringify(filters)]);

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setFilters({
      search: '',
      level: '',
      source: '',
      dateRange: [],
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getLevelTag = (level) => {
    const colorMap = {
      error: 'red',
      warning: 'orange',
      info: 'blue',
      debug: 'purple',
    };
    return <Tag color={colorMap[level] || 'default'}>{level.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text) => format(parseISO(text), 'yyyy-MM-dd HH:mm:ss'),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      render: (level) => getLevelTag(level),
      filters: [
        { text: 'Error', value: 'error' },
        { text: 'Warning', value: 'warning' },
        { text: 'Info', value: 'info' },
        { text: 'Debug', value: 'debug' },
      ],
      onFilter: (value, record) => record.level === value,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      filters: [
        { text: 'API', value: 'API' },
        { text: 'Frontend', value: 'Frontend' },
        { text: 'Backend', value: 'Backend' },
        { text: 'Database', value: 'Database' },
        { text: 'Auth', value: 'Auth' },
      ],
      onFilter: (value, record) => record.source === value,
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (_, record) => <UserCell userId={record.user_id} />
    },
  ];

  return (
    <Card title="System Logs" className="system-logs-card">
      <div className="mb-4">
        <Space size="middle" className="w-full mb-4" wrap>
          <Input
            placeholder="Search logs..."
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 250 }}
            onPressEnter={handleSearch}
          />
          
          <Select
            placeholder="Filter by level"
            style={{ width: 150 }}
            allowClear
            value={filters.level || null}
            onChange={(value) => setFilters(prev => ({ ...prev, level: value || '' }))}
          >
            <Option value="error">Error</Option>
            <Option value="warning">Warning</Option>
            <Option value="info">Info</Option>
            <Option value="debug">Debug</Option>
          </Select>
          
          <Select
            placeholder="Filter by source"
            style={{ width: 150 }}
            allowClear
            value={filters.source || null}
            onChange={(value) => setFilters(prev => ({ ...prev, source: value || '' }))}
          >
            <Option value="API">API</Option>
            <Option value="Frontend">Frontend</Option>
            <Option value="Backend">Backend</Option>
            <Option value="Database">Database</Option>
            <Option value="Auth">Auth</Option>
          </Select>
          
          <RangePicker
            showTime={{ format: 'HH:mm:ss' }}
            format="YYYY-MM-DD HH:mm:ss"
            onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
            value={filters.dateRange}
            style={{ width: 350 }}
          />
          
          <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
            Search
          </Button>
          
          <Button onClick={handleReset} icon={<ReloadOutlined />}>
            Reset
          </Button>
        </Space>
      </div>
      
      <Table
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, pageSize) => {
            setPagination(prev => ({
              ...prev,
              current: page,
              pageSize: pageSize,
            }));
          },
        }}
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
};

export default SystemLogs;
